// Einsatz-Kalender (15.10.2026, Betreiber: „extra Kalender nur für Einsätze und Check-up mit einer Slide-Funktion im
// allgemeinen Kalender“): Wochenplan mit Uhrzeiten — Kurse mit Trainer (Kursplan), Personal Training (pt_termine) und
// Check-Ups (Anfragen + Termine, checkup_kalender). Nur Anzeige und dieselben Aktionen wie im Tagespanel; Wischen oder
// Pfeile blättern (Handy: Tag für Tag, sonst Woche für Woche).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarPlus, ChevronLeft, ChevronRight, Dumbbell, Stethoscope, UserCheck, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { buchungsfensterAPI, calendarAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import BuchungsFenster from '@/components/BuchungsFenster';
import { Button } from '@/components/ui/button';
import { CheckupZeile } from '@/components/CheckupKalender';
import { PtTerminZeile } from '@/components/TrainerVerwaltung';
import EinsatzKalenderAbo from '@/components/EinsatzKalenderAbo';

const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const VON_H = 6, BIS_H = 22, PX_H = 52;
const FENSTER = { vormittag: ['09:00', '12:00'], nachmittag: ['14:00', '17:00'], abend: ['17:00', '20:00'] };
const ART = {
  kurs: { label: 'Kurse', klasse: 'bg-slate-100 dark:bg-slate-800/60 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200' },
  pt: { label: 'Personal Training', klasse: 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-900 dark:text-red-200' },
  checkup: { label: 'Check-Ups', klasse: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-400 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200' },
  gespraech: { label: 'Gespräche', klasse: 'bg-violet-50 dark:bg-violet-900/20 border-violet-400 dark:border-violet-800 text-violet-900 dark:text-violet-200' },
  anfrage: { label: 'Anfragen', klasse: 'bg-amber-50/80 dark:bg-amber-900/20 border-amber-400 border-dashed text-amber-900 dark:text-amber-200' },
};

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const montagVon = (d) => { const m = new Date(d.getFullYear(), d.getMonth(), d.getDate()); m.setDate(m.getDate() - ((m.getDay() + 6) % 7)); return m; };
export const plusTage = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const minuten = (hhmm) => { const [h, m] = String(hhmm || '00:00').split(':').map(Number); return h * 60 + (m || 0); };
export const kw = (d) => { const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())); const n = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - n); const j = new Date(Date.UTC(t.getUTCFullYear(), 0, 1)); return Math.ceil(((t - j) / 86400000 + 1) / 7); };

// Überlappende Termine eines Tages nebeneinander legen (Spuren) — auch vom Coach-Kalender (pages/CoachKalender.js) genutzt
export function spuren(liste) {
  const sortiert = [...liste].sort((a, b) => a.von - b.von || b.bis - a.bis);
  const enden = [];
  sortiert.forEach((e) => {
    let s = enden.findIndex((ende) => ende <= e.von);
    if (s < 0) { s = enden.length; enden.push(0); }
    enden[s] = e.bis; e.spur = s;
  });
  sortiert.forEach((e) => { e.spuren = Math.max(1, ...sortiert.filter((x) => x.von < e.bis && x.bis > e.von).map((x) => x.spur + 1)); });
  return sortiert;
}

export default function EinsatzKalender({ darfBearbeiten }) {
  const [montag, setMontag] = useState(() => montagVon(new Date()));
  const [tagIdx, setTagIdx] = useState(() => (new Date().getDay() + 6) % 7);
  const [kurse, setKurse] = useState([]);
  const [pt, setPt] = useState([]);
  const [checkups, setCheckups] = useState([]);
  const [gespraeche, setGespraeche] = useState([]);
  const [fenster, setFenster] = useState(null);
  const { user } = useAuth();
  const leitung = ['admin', 'manager'].includes(user?.role);
  const [filter, setFilter] = useState({ kurs: true, pt: true, checkup: true, gespraech: true, anfrage: true });
  const [trainer, setTrainer] = useState('');
  const [auswahl, setAuswahl] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const touch = useRef(null);
  const nr = useRef(0);

  const laden = useCallback(async () => {
    const anfrage = ++nr.current;
    setLaedt(true);
    const von = iso(montag), bis = iso(plusTage(montag, 6));
    try {
      const [kal, kp, gp] = await Promise.all([
        calendarAPI.getEvents({ start: `${von}T00:00:00`, end: `${bis}T23:59:59` }),
        api.get('/kursplan/courses', { params: { week_date: von } }).catch(() => ({ data: { courses: [] } })),
        buchungsfensterAPI.gespraeche({ von, bis }).catch(() => ({ data: { gespraeche: [] } })),
      ]);
      if (anfrage !== nr.current) return;
      setPt(kal.data.pt_termine || []);
      setCheckups(kal.data.checkups || []);
      setKurse(kp.data.courses || []);
      setGespraeche(gp.data.gespraeche || []);
    } finally { if (anfrage === nr.current) setLaedt(false); }
  }, [montag]);
  useEffect(() => { laden(); }, [laden]);

  const tage = useMemo(() => TAGE.map((t, i) => ({ kurz: t, datum: plusTage(montag, i) })), [montag]);
  const heute = iso(new Date());

  const termine = useMemo(() => {
    const raus = [];
    kurse.forEach((k) => {
      if (k.category === 'parking') return;
      const d = iso(plusTage(montag, k.day_index || 0));
      raus.push({ id: `k-${k.id}`, art: 'kurs', datum: d, von: minuten(k.start), bis: minuten(k.end), start: k.start, ende: k.end,
        titel: k.name, unter: k.trainer || 'ohne Trainer', trainer: k.trainer || '', ausfall: !!k.ausfall,
        zusatz: `${k.erwartet_spots ?? k.booked_spots ?? 0}/${k.max_spots} erwartet`, roh: k });
    });
    pt.forEach((t) => raus.push({ id: `p-${t.id}`, art: 'pt', datum: t.datum, von: minuten(t.start), bis: minuten(t.ende), start: t.start, ende: t.ende,
      titel: `PT · ${t.kunde || 'ohne Name'}`, trainer: t.trainer_name || '', roh: t,
      unter: `${t.trainer_name}${t.status === 'angefragt' ? ' · wartet' : t.status === 'abgelehnt' ? ' · abgelehnt' : ''}`,
      zustand: t.status === 'angefragt' ? 'wartet' : t.status === 'abgelehnt' ? 'abgelehnt' : null }));
    // Mitarbeitergespräche: die Leitung sieht Person und Thema, der Eingeladene nur Zeit und Art (entscheidet der Server)
    gespraeche.forEach((g) => raus.push({ id: `g-${g.id}`, art: 'gespraech', datum: g.datum, von: minuten(g.start), bis: minuten(g.ende), start: g.start, ende: g.ende,
      titel: 'Mitarbeitergespräch', unter: g.person?.name || '', trainer: '', roh: g }));
    checkups.forEach((c) => {
      if (c.status === 'offen') {
        const [s, e] = FENSTER[c.zeitfenster] || ['10:00', '11:00'];
        raus.push({ id: `a-${c.id}`, art: 'anfrage', datum: c.datum, von: minuten(s), bis: minuten(e), start: s, ende: e,
          titel: `Anfrage · ${c.profil_name}`, unter: `Wunsch ${c.zeitfenster_text || ''}`, trainer: '', roh: c });
      } else if (c.start) {
        raus.push({ id: `c-${c.id}`, art: 'checkup', datum: c.datum, von: minuten(c.start), bis: minuten(c.ende || c.start) || minuten(c.start) + 30,
          start: c.start, ende: c.ende, titel: `Check-Up · ${c.profil_name}`, trainer: c.trainer_name || '', roh: c,
          unter: c.status === 'erledigt' ? 'durchgeführt' : (c.trainer_name || 'noch kein Trainer') });
      }
    });
    return raus;
  }, [kurse, pt, checkups, gespraeche, montag]);

  const trainerListe = useMemo(() => [...new Set(termine.map((t) => t.trainer).filter(Boolean))].sort(), [termine]);
  const sichtbar = termine.filter((t) => filter[t.art] && (!trainer || !t.trainer || t.trainer.toLowerCase().includes(trainer.toLowerCase())));
  const proTag = useMemo(() => tage.map((t) => spuren(sichtbar.filter((e) => e.datum === iso(t.datum)).map((e) => ({ ...e })))), [tage, sichtbar]);
  const offen = checkups.filter((c) => c.status === 'offen');

  const woche = (n) => { setMontag((m) => plusTage(m, 7 * n)); setAuswahl(null); };
  const tag = (n) => {
    const neu = tagIdx + n;
    if (neu < 0) { woche(-1); setTagIdx(6); } else if (neu > 6) { woche(1); setTagIdx(0); } else setTagIdx(neu);
  };
  const handy = () => typeof window !== 'undefined' && window.innerWidth < 640;
  const wischStart = (e) => { touch.current = e.touches[0].clientX; };
  const wischEnde = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current; touch.current = null;
    if (Math.abs(dx) < 60) return;
    const richtung = dx < 0 ? 1 : -1;
    handy() ? tag(richtung) : woche(richtung);
  };

  // Klick in eine freie Stelle: PT-Termin an diesem Tag zu dieser Uhrzeit eintragen (auf 15 Minuten gerundet)
  const freiGeklickt = (i, ev) => {
    if (!darfBearbeiten) return;
    const y = ev.clientY - ev.currentTarget.getBoundingClientRect().top;
    const m = Math.min(BIS_H * 60 - 60, Math.max(VON_H * 60, Math.round((VON_H * 60 + y / PX_H * 60) / 15) * 15));
    setFenster({ art: 'pt', datum: iso(tage[i].datum), start: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}` });
  };

  const spalte = (i) => (
    <div key={i} className={`relative border-l border-border/40 ${darfBearbeiten ? 'cursor-cell' : ''}`} style={{ height: (BIS_H - VON_H) * PX_H }}
      onClick={(ev) => freiGeklickt(i, ev)} title={darfBearbeiten ? 'Klicken: Termin buchen' : undefined}>
      {Array.from({ length: BIS_H - VON_H }).map((_, h) => <div key={h} className="absolute left-0 right-0 border-t border-border/30" style={{ top: h * PX_H }} />)}
      {iso(tage[i].datum) === heute && (() => {
        const jetzt = new Date(); const m = jetzt.getHours() * 60 + jetzt.getMinutes();
        return m >= VON_H * 60 && m <= BIS_H * 60 ? <div className="absolute left-0 right-0 z-20 border-t-2 border-marke" style={{ top: (m - VON_H * 60) / 60 * PX_H }} /> : null;
      })()}
      {proTag[i].map((e) => {
        const top = Math.max(0, (e.von - VON_H * 60) / 60 * PX_H);
        const hoehe = Math.max(20, (Math.min(e.bis, BIS_H * 60) - Math.max(e.von, VON_H * 60)) / 60 * PX_H - 2);
        const breite = 100 / e.spuren;
        return (
          <button key={e.id} type="button" onClick={(ev) => { ev.stopPropagation(); setAuswahl(e); }} data-testid={`einsatz-${e.id}`}
            className={`absolute z-10 rounded-md border px-1.5 py-1 text-left overflow-hidden hover:ring-2 hover:ring-marke/40 ${ART[e.art].klasse} ${e.ausfall || e.zustand === 'abgelehnt' ? 'opacity-50 line-through' : ''} ${e.zustand === 'wartet' ? 'border-dashed' : ''} ${auswahl?.id === e.id ? 'ring-2 ring-marke' : ''}`}
            style={{ top, height: hoehe, left: `calc(${e.spur * breite}% + 2px)`, width: `calc(${breite}% - 4px)` }}>
            <p className="text-[10px] font-semibold leading-tight truncate">{e.titel}</p>
            {hoehe > 30 && <p className="text-[9px] leading-tight opacity-80 truncate">{e.start}{e.ende ? `–${e.ende}` : ''} · {e.unter}</p>}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="einsatz-kalender">
      {/* Kopf: Woche, Filter, Trainer */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-card border border-border rounded-lg">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => (handy() ? tag(-1) : woche(-1))}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold px-1 min-w-[150px] text-center">
            KW {kw(montag)} · {montag.getDate()}.{montag.getMonth() + 1}.–{plusTage(montag, 6).getDate()}.{plusTage(montag, 6).getMonth() + 1}.
          </span>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => (handy() ? tag(1) : woche(1))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setMontag(montagVon(new Date())); setTagIdx((new Date().getDay() + 6) % 7); }}>Heute</Button>
        <div className="flex flex-wrap gap-1">
          {Object.entries(ART).map(([k, a]) => (
            <button key={k} type="button" onClick={() => setFilter((f) => ({ ...f, [k]: !f[k] }))}
              className={`h-8 px-2.5 rounded-full border text-xs ${filter[k] ? a.klasse : 'border-border text-muted-foreground line-through'}`}>
              {a.label}{k === 'anfrage' && offen.length ? ` (${offen.length})` : ''}
            </button>
          ))}
        </div>
        {trainerListe.length > 0 && (
          <select value={trainer} onChange={(e) => setTrainer(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs ml-auto" data-testid="einsatz-trainer">
            <option value="">Alle Trainer</option>
            {trainerListe.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {darfBearbeiten && (
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setFenster({ art: 'pt', datum: iso(tage[tagIdx]?.datum || new Date()), start: '' })} data-testid="bf-oeffnen">
            <CalendarPlus className="w-3.5 h-3.5" />Termin buchen</Button>
        )}
        <EinsatzKalenderAbo />
      </div>
      <BuchungsFenster offen={!!fenster} onClose={() => setFenster(null)} datum={fenster?.datum} start={fenster?.start} art={fenster?.art} onGebucht={laden} />

      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex-1 min-w-0 rounded-xl border border-border/60 bg-card overflow-hidden" onTouchStart={wischStart} onTouchEnd={wischEnde}>
          {/* Tageskopf */}
          <div className="grid border-b border-border" style={{ gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))' }}>
            <div />
            {tage.map((t, i) => {
              const d = iso(t.datum);
              return (
                <button key={i} type="button" onClick={() => setTagIdx(i)}
                  className={`py-2 text-center text-[11px] ${i !== tagIdx ? 'hidden sm:block' : ''} ${d === heute ? 'text-marke font-bold' : 'text-muted-foreground font-medium'}`}>
                  {t.kurz} {t.datum.getDate()}.{t.datum.getMonth() + 1}.
                </button>
              );
            })}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            <div className="grid" style={{ gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))' }}>
              <div className="relative" style={{ height: (BIS_H - VON_H) * PX_H }}>
                {Array.from({ length: BIS_H - VON_H }).map((_, h) => (
                  <span key={h} className="absolute right-1 text-[9px] text-muted-foreground -translate-y-1/2" style={{ top: h * PX_H }}>{h ? `${VON_H + h}:00` : ''}</span>
                ))}
              </div>
              {tage.map((_, i) => <div key={i} className={i !== tagIdx ? 'hidden sm:block' : 'col-span-7 sm:col-span-1'}>{spalte(i)}</div>)}
            </div>
          </div>
          {laedt && <p className="text-[11px] text-muted-foreground px-3 py-1.5 border-t border-border">Lädt …</p>}
        </div>

        {/* Auswahl: Details und Aktionen */}
        <div className="lg:w-[320px]">
          <div className="rounded-xl border border-border/60 bg-card p-3 sticky top-4 space-y-2">
            {!auswahl ? (
              <>
                <p className="text-sm font-semibold">Einsätze dieser Woche</p>
                <p className="text-xs text-muted-foreground">
                  {sichtbar.filter((t) => t.art === 'kurs' && !t.ausfall).length} Kurse · {sichtbar.filter((t) => t.art === 'pt').length} PT ·{' '}
                  {sichtbar.filter((t) => t.art === 'checkup').length} Check-Ups · {offen.length} Anfrage{offen.length === 1 ? '' : 'n'} offen
                </p>
                {offen.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-400">Gestrichelte Felder sind Check-Up-Anfragen im Wunsch-Zeitfenster. Antippen zum Freigeben.</p>}
                {darfBearbeiten && <p className="text-[11px] text-muted-foreground">„Termin buchen“ oder Klick in eine freie Stelle: Das Fenster zeigt die freien Lücken und warnt vor Überschneidungen. Während eines Kurses: Kurs antippen, dann „PT“ oder „Check-Up zu dieser Zeit“.</p>}
                <p className="text-[11px] text-muted-foreground">Wischen oder Pfeile: {handy() ? 'nächster Tag' : 'nächste Woche'}.</p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground">
                    {TAGE[(new Date(auswahl.datum).getDay() + 6) % 7]}, {auswahl.datum.split('-').reverse().slice(0, 2).join('.')}.
                  </p>
                  <button type="button" onClick={() => setAuswahl(null)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
                </div>
                {auswahl.art === 'kurs' && (
                  <div className="rounded-lg border border-border/60 p-2.5 space-y-1">
                    <p className="text-sm font-semibold flex items-center gap-1.5"><Users className="w-4 h-4" />{auswahl.titel}</p>
                    <p className="text-xs text-muted-foreground">{auswahl.start}–{auswahl.ende} Uhr · {auswahl.unter}</p>
                    <p className="text-xs text-muted-foreground">{auswahl.zusatz}{auswahl.ausfall ? ' · fällt aus' : ''}</p>
                    <a href="/kursplan" className="text-xs text-marke hover:underline">Im Kursplan öffnen</a>
                    {darfBearbeiten && (
                      <div className="flex gap-1.5 pt-1.5">
                        {[['pt', 'PT zu dieser Zeit'], ['checkup', 'Check-Up zu dieser Zeit']].map(([k, l]) => (
                          <Button key={k} size="sm" variant="outline" className="h-7 text-xs flex-1"
                            onClick={() => setFenster({ art: k, datum: auswahl.datum, start: auswahl.start })}>{l}</Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {auswahl.art === 'gespraech' && (
                  <div className="rounded-lg border border-border/60 p-2.5 space-y-1" data-testid="gespraech-detail">
                    <p className="text-sm font-semibold flex items-center gap-1.5"><UserCheck className="w-4 h-4" />Mitarbeitergespräch</p>
                    <p className="text-xs text-muted-foreground">{auswahl.start}–{auswahl.ende} Uhr</p>
                    {leitung && auswahl.roh.person?.name && <p className="text-xs">Mit {auswahl.roh.person.name}</p>}
                    {leitung && auswahl.roh.thema && <p className="text-xs">Thema: {auswahl.roh.thema}</p>}
                    {leitung && auswahl.roh.notizen && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{auswahl.roh.notizen}</p>}
                    {leitung && (
                      <Button size="sm" variant="outline" className="h-7 text-xs mt-1" onClick={async () => {
                        const grund = window.prompt('Gespräch absagen — Grund (optional):');
                        if (grund === null) return;
                        try { await buchungsfensterAPI.gespraechAbsagen(auswahl.roh.id, { grund }); toast.success('Gespräch abgesagt'); setAuswahl(null); laden(); }
                        catch (e) { toast.error(e?.response?.data?.detail || 'Absagen fehlgeschlagen'); }
                      }}>Absagen</Button>
                    )}
                  </div>
                )}
                {auswahl.art === 'pt' && <PtTerminZeile t={auswahl.roh} darfBearbeiten={darfBearbeiten} onChange={() => { setAuswahl(null); laden(); }} />}
                {(auswahl.art === 'checkup' || auswahl.art === 'anfrage') && (
                  <CheckupZeile c={auswahl.roh} darfBearbeiten={darfBearbeiten} onChange={() => { setAuswahl(null); laden(); }} />
                )}
              </>
            )}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />Kurs</span>
              <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3 text-marke" />PT</span>
              <span className="flex items-center gap-1"><Stethoscope className="w-3 h-3 text-emerald-600" />Check-Up</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm border border-dashed border-amber-500 bg-amber-100" />Anfrage</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
